import { apiClient } from "@/lib/api-client";
import type {
  BillingInterval,
  CreateSubscriptionResponseDto,
  PayFastRedirectResultDto,
} from "../dtos/billing-plan.dto"
import type {
  SubscriptionStatusDto,
  PendingSubscriptionDto,
} from "../dtos/subscription-status.dto"
import type { PaymentHistoryDto } from "../dtos/payment-history.dto"
import type { GracePeriodInfoDto } from "../dtos/grace-period-info.dto"
import type { SubscriptionDto } from "../dtos/billing-plan.dto"

export interface CreateSubscriptionRequestDto {
  planId: string
  billingInterval: BillingInterval
  organizationId?: string
  couponCode?: string
}

// 1 = Monthly, 12 = Annual
export type ActivateBillingInterval = 1 | 12

export interface ActivateSubscriptionRequestDto {
  planId: string
  billingInterval: ActivateBillingInterval
  organizationId: string
  couponCode?: string | null
}

export async function createSubscription(
  request: CreateSubscriptionRequestDto,
) {
  const { data } = await apiClient.post<CreateSubscriptionResponseDto>(
    "/subscription/create",
    request,
  )
  return data
}

export async function activateSubscription(
  request: ActivateSubscriptionRequestDto,
): Promise<CreateSubscriptionResponseDto> {
  const { data } = await apiClient.post<CreateSubscriptionResponseDto>(
    "/subscription/activate",
    request,
  )
  return data
}

export async function getActiveSubscription(organizationId?: string) {
  const { data } = await apiClient.get("/api/subscription/active", {
    params: { organizationId },
  })
  return data
}

export async function getSubscriptionStatus(
  organizationId?: string,
): Promise<SubscriptionStatusDto> {
  const { data } = await apiClient.get<SubscriptionStatusDto>(
    "/subscription/status",
    {
      params: { organizationId },
    },
  )
  return data
}

export async function getPaymentHistory(
  subscriptionId: string,
): Promise<PaymentHistoryDto[]> {
  const { data } = await apiClient.get<PaymentHistoryDto[]>(
    `/subscription/payment-history`,
    {
      params: { subscriptionId },
    },
  )
  return data
}

export async function getSubscriptionHistory(): Promise<SubscriptionDto[]> {
  const { data } = await apiClient.get<SubscriptionDto[]>(
    "/subscription/history",
  )
  return data
}

export async function getGracePeriodInfo(): Promise<GracePeriodInfoDto> {
  const { data } = await apiClient.get<GracePeriodInfoDto>(
    "/subscription/grace-period-info",
  )
  return data
}

export async function cancelSubscription(
  subscriptionId: string,
  immediate: boolean = false,
): Promise<CreateSubscriptionResponseDto> {
  const { data } = await apiClient.post<CreateSubscriptionResponseDto>(
    `/subscription/${subscriptionId}/cancel`,
    null,
    { params: { immediate } },
  )
  return data
}

export async function changePlan(
  subscriptionId: string,
  newPlanId: string,
): Promise<CreateSubscriptionResponseDto> {
  const { data } = await apiClient.post<CreateSubscriptionResponseDto>(
    `/subscription/${subscriptionId}/change-plan`,
    { newPlanId },
  )
  return data
}

export async function getPendingSubscription(
  organizationId: string,
): Promise<PendingSubscriptionDto> {
  const { data } = await apiClient.get<PendingSubscriptionDto>(
    "/subscription/pending",
    { params: { organizationId } },
  )
  return data
}

export async function initiatePayment(
  subscriptionId: string,
): Promise<{ paymentRedirect: PayFastRedirectResultDto }> {
  const { data } = await apiClient.post<{
    paymentRedirect: PayFastRedirectResultDto
  }>(`/subscription/${subscriptionId}/initiate-payment`)
  return data
}
